import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AgentChatError,
  AgentChatEvent,
  AgentConversationStatus,
  AgentConversationSummary,
  AgentFeedbackRating,
  AgentMessage,
} from 'src/app/types/agent-chat';
import { AgentChatService } from './agent-chat.service';
import { AgentChatStreamService } from './agent-chat-stream.service';

/** What the agent is doing while it works, from the tool it's running; shown instead of tool names. */
export type AgentActivity = 'articles' | 'store' | 'code' | 'team';

const STORE_TOOLS = new Set([
  'list_charts',
  'get_chart',
  'list_chart_products',
  'check_chart_coverage',
  'search_products',
  'list_categories',
  'list_dimensions',
  'get_plan_and_usage',
  'get_app_embed_status',
]);

export function activityOf(tool: string): AgentActivity | null {
  if (tool === 'read_article') return 'articles';
  if (tool === 'ask_code') return 'code';
  if (tool === 'request_human') return 'team';

  return STORE_TOOLS.has(tool) ? 'store' : null;
}

/** The agent's reply while it streams. */
export interface PendingReply {
  text: string;
  tools: string[];
  activity: AgentActivity | null;
}

/** A message that didn't go through; `content` is what the merchant wrote, for the retry. */
export interface AgentChatFailure {
  /** The API's code (`AGENT_RATE_LIMITED`, `AGENT_DAILY_CAP_REACHED`, `AGENT_DISABLED`, `AGENT_REPLY_FAILED`), or `UNKNOWN`. */
  code: string;
  retryAfterSeconds?: number;
  content: string;
}

export type AgentChatView = 'chat' | 'history';

/** How many past conversations the history shows. */
export const AGENT_HISTORY_LIMIT = 30;

const OPEN_KEY = 'goodchart-agent-chat-open';
const EXPANDED_KEY = 'goodchart-agent-chat-expanded';

/** While a person has the conversation, the agent stays out of it and messages wait for the team. */
const WITH_THE_TEAM: AgentConversationStatus[] = ['needs_follow_up', 'with_admin'];

/**
 * The support chat's state (GOO-457): whether the panel is open (remembered for the session), the
 * conversation on screen and the reply streaming into it, the history, and the launcher's unread
 * badge. No polling: the badge refreshes on load and whenever the panel opens.
 *
 * A person is reached only through the agent (GOO-465): when it hands the conversation over, or
 * the merchant opens one the team has, the chat waits for the team (GOO-458). With the agent off,
 * the chat takes no new questions, but a conversation already with the team stays open.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentChatStore {
  private readonly api = inject(AgentChatService);
  private readonly stream = inject(AgentChatStreamService);
  private turn?: Subscription;

  readonly open = signal(sessionStorage.getItem(OPEN_KEY) === 'true');
  /** Whether the panel is enlarged for easier reading; remembered for the session like `open`. */
  readonly expanded = signal(sessionStorage.getItem(EXPANDED_KEY) === 'true');
  /** Whether the agent answers; assumed until the status says otherwise. */
  readonly enabled = signal(true);
  readonly view = signal<AgentChatView>('chat');
  readonly unreadCount = signal(0);
  readonly conversationId = signal<string | undefined>(undefined);
  /** Who answers the conversation on screen; `null` for a new one. */
  readonly status = signal<AgentConversationStatus | null>(null);
  readonly messages = signal<AgentMessage[]>([]);
  readonly pending = signal<PendingReply | null>(null);
  readonly failure = signal<AgentChatFailure | null>(null);
  readonly conversations = signal<AgentConversationSummary[]>([]);
  readonly loading = signal(false);

  readonly sending = computed(() => this.pending() !== null);
  readonly withTeam = computed(() => WITH_THE_TEAM.includes(this.status() as AgentConversationStatus));
  /** With the agent off, only a conversation the team has still takes messages. */
  readonly canSend = computed(() => this.withTeam() || this.enabled());

  setOpen(open: boolean) {
    this.open.set(open);
    sessionStorage.setItem(OPEN_KEY, String(open));

    if (open) this.refreshStatus();
  }

  toggle() {
    this.setOpen(!this.open());
  }

  toggleExpanded() {
    this.expanded.set(!this.expanded());
    sessionStorage.setItem(EXPANDED_KEY, String(this.expanded()));
  }

  refreshStatus() {
    this.api.getStatus().subscribe({
      next: ({ enabled, unreadCount }) => {
        this.enabled.set(enabled);
        this.unreadCount.set(unreadCount);
      },
      // The badge is a nicety; a failed check leaves it as it was.
      error: () => undefined,
    });
  }

  showHistory() {
    this.view.set('history');
    this.loading.set(true);
    this.api.getConversations({ skip: 0, limit: AGENT_HISTORY_LIMIT }).subscribe({
      next: ({ data }) => {
        this.conversations.set(data);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  showChat() {
    this.view.set('chat');
  }

  /** An empty chat; a reply still streaming is stopped, and the agent saves nothing of it. */
  startNew() {
    this.stopTurn();
    this.conversationId.set(undefined);
    this.status.set(null);
    this.messages.set([]);
    this.failure.set(null);
    this.view.set('chat');
  }

  openConversation(id: string) {
    this.stopTurn();
    this.failure.set(null);
    this.view.set('chat');
    this.loading.set(true);
    this.api.getConversation(id).subscribe({
      next: (conversation) => {
        this.conversationId.set(conversation._id);
        this.status.set(conversation.status);
        this.messages.set(conversation.messages);
        this.loading.set(false);
        // Opening it marked the team's replies as read.
        this.conversations.update((list) => list.map((item) => (item._id === id ? { ...item, unread: false } : item)));
        this.refreshStatus();
      },
      error: () => this.loading.set(false),
    });
  }

  send(text: string) {
    const content = text.trim();

    if (!content || this.sending() || !this.canSend()) return;

    this.failure.set(null);
    this.messages.update((messages) => [...messages, { role: 'user', content }]);
    this.pending.set({ text: '', tools: [], activity: null });

    this.turn = this.stream.send(this.conversationId(), content).subscribe({
      next: (event) => this.apply(event, content),
      error: (error: unknown) =>
        error instanceof AgentChatError
          ? this.fail(content, error.code || 'UNKNOWN', error.retryAfterSeconds)
          : this.fail(content, 'UNKNOWN'),
      // A stream that ends without `done` was cut off; the agent saved nothing.
      complete: () => {
        if (this.pending()) this.fail(content, 'AGENT_REPLY_FAILED');
      },
    });
  }

  /**
   * Rates one of the agent's replies, showing it at once and taking it back if it doesn't save. A
   * comment comes with a thumbs down.
   */
  rate(messageId: string, rating: AgentFeedbackRating, comment?: string) {
    const conversationId = this.conversationId();
    const before = this.messages().find(({ _id }) => _id === messageId)?.feedback;

    if (!conversationId) return;

    const setFeedback = (feedback: AgentMessage['feedback']) =>
      this.messages.update((messages) =>
        messages.map((message) => (message._id === messageId ? { ...message, feedback } : message)),
      );
    const trimmed = comment?.trim();

    setFeedback({ rating, ...(trimmed && { comment: trimmed }) });
    this.api
      .setFeedback(conversationId, messageId, { rating, ...(trimmed && { comment: trimmed }) })
      .subscribe({ error: () => setFeedback(before) });
  }

  /** Sends the failed message again; the failed attempt left nothing saved. */
  retry() {
    const failure = this.failure();

    if (failure) this.send(failure.content);
  }

  private apply(event: AgentChatEvent, content: string) {
    switch (event.type) {
      case 'start':
        this.conversationId.set(event.conversationId);
        // A new or resolved conversation is the agent's once it answers.
        this.status.set('agent');
        break;
      case 'human-requested':
        // The agent handed the conversation to the team; its reply says so.
        this.status.set('needs_follow_up');
        break;
      case 'tool':
        this.pending.update(
          (reply) =>
            reply && {
              ...reply,
              tools: [...reply.tools, event.name],
              activity: activityOf(event.name) ?? reply.activity,
            },
        );
        break;
      case 'text':
        this.pending.update((reply) => reply && { ...reply, text: reply.text + event.delta, activity: null });
        break;
      case 'done': {
        const reply = this.pending();

        this.conversationId.set(event.conversationId);
        this.messages.update((messages) => [
          ...messages,
          { _id: event.messageId, role: 'assistant', content: reply?.text ?? '', tools: reply?.tools ?? [] },
        ]);
        this.pending.set(null);
        break;
      }
      case 'stored':
        // A person has the conversation: the message waits for them, and no reply streams.
        this.conversationId.set(event.conversationId);
        this.messages.update((messages) => [...messages.slice(0, -1), event.message]);
        this.pending.set(null);
        break;
      case 'error':
        this.fail(content, event.message || 'AGENT_REPLY_FAILED');
        break;
    }
  }

  /** Takes the unsent message back off the list, keeping it for the retry. */
  private fail(content: string, code: string, retryAfterSeconds?: number) {
    // The agent was turned off since the status was read.
    if (code === 'AGENT_DISABLED') this.enabled.set(false);
    this.pending.set(null);
    this.messages.update((messages) => messages.slice(0, -1));
    this.failure.set({ code, retryAfterSeconds, content });
  }

  private stopTurn() {
    this.turn?.unsubscribe();
    this.turn = undefined;
    this.pending.set(null);
  }
}
